import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useSocket } from "./SocketContext";
import { getMyDataCompleteness } from "../services/dataCompletenessApi";
import type { MyDataCompleteness } from "../types/dataCompleteness";

const POLL_INTERVAL_MS = 60_000;

interface DataCompletenessContextValue {
  /** Null until the first load (and when the API is unreachable). */
  data: MyDataCompleteness | null;
  /** Items the current user still needs to fill in. */
  outstanding: number;
  refresh: () => Promise<void>;
  dialogOpen: boolean;
  openDialog: () => void;
  closeDialog: () => void;
}

const DataCompletenessContext =
  createContext<DataCompletenessContextValue | null>(null);

/**
 * The current user's data traffic light: killed deals without a kill reason
 * and deals in closing without advisor fee quotes, for deals they lead.
 * Refreshes on focus, every minute, and when deals or fees change.
 */
export function DataCompletenessProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { subscribe } = useSocket();
  const [data, setData] = useState<MyDataCompleteness | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const refresh = useCallback(async () => {
    const response = await getMyDataCompleteness();
    if (response.success && response.data) setData(response.data);
  }, []);

  useEffect(() => {
    void refresh();
    const tick = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    const interval = setInterval(tick, POLL_INTERVAL_MS);
    const onFocus = () => void refresh();
    window.addEventListener("focus", onFocus);
    const unsubscribers = [
      subscribe("deal_updated", onFocus),
      subscribe("advisor_fees_updated", onFocus),
    ];
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [refresh, subscribe]);

  const outstanding = data
    ? data.counts.missing_kill_reason + data.counts.missing_advisor_fees
    : 0;

  const value = useMemo(
    () => ({
      data,
      outstanding,
      refresh,
      dialogOpen,
      openDialog: () => setDialogOpen(true),
      closeDialog: () => setDialogOpen(false),
    }),
    [data, outstanding, refresh, dialogOpen],
  );

  return (
    <DataCompletenessContext.Provider value={value}>
      {children}
    </DataCompletenessContext.Provider>
  );
}

/** Null outside the provider (e.g. a toolbar rendered on its own in a test). */
export function useOptionalDataCompleteness() {
  return useContext(DataCompletenessContext);
}

export function useDataCompleteness() {
  const ctx = useContext(DataCompletenessContext);
  if (!ctx) {
    throw new Error(
      "useDataCompleteness must be used within DataCompletenessProvider",
    );
  }
  return ctx;
}
